
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import { Edit, Trash2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import { ImageUpload } from "@/components/vehicle/ImageUpload";

interface VehicleImageProps {
  imageUrl: string | null;
  vehicleId: string;
  onImageUpdate: () => void;
}

const VehicleImage = ({ imageUrl, vehicleId, onImageUpdate }: VehicleImageProps) => {
  const [showImageUpload, setShowImageUpload] = useState(false);
  const { toast } = useToast();

  const handleImageUploaded = async (url: string) => {
    try {
      const { error } = await supabase
        .from("vehicles")
        .update({ image_url: url })
        .eq("id", vehicleId);

      if (error) throw error;

      toast({
        title: "Afbeelding bijgewerkt",
        description: "De voertuig afbeelding is succesvol bijgewerkt.",
      });
      
      setShowImageUpload(false);
      onImageUpdate();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Fout bij bijwerken",
        description: error.message,
      });
    }
  };

  const handleDeleteImage = async () => {
    try {
      const { error } = await supabase
        .from("vehicles")
        .update({ image_url: null })
        .eq("id", vehicleId);

      if (error) throw error;

      toast({
        title: "Afbeelding verwijderd",
        description: "De voertuig afbeelding is succesvol verwijderd.",
      });
      
      onImageUpdate();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Fout bij verwijderen",
        description: error.message,
      });
    }
  };

  return (
    <div className="bg-white rounded-lg overflow-hidden shadow-sm">
      <div className="relative w-full h-[275px] bg-gray-100">
        {imageUrl ? (
          <>
            <img
              src={imageUrl}
              alt="Vehicle"
              className="w-full h-full object-cover"
            />
            <div className="absolute top-4 right-4 flex gap-2">
              <Dialog open={showImageUpload} onOpenChange={setShowImageUpload}>
                <DialogTrigger asChild>
                  <Button variant="secondary" size="sm" className="h-9 w-9 p-0">
                    <Edit className="h-4 w-4" />
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Voertuig Afbeelding Bewerken</DialogTitle>
                    <DialogDescription>
                      Upload een nieuwe afbeelding voor je voertuig
                    </DialogDescription>
                  </DialogHeader>
                  <ImageUpload onImageUploaded={handleImageUploaded} />
                </DialogContent>
              </Dialog>
              <Button
                variant="destructive"
                size="sm"
                className="h-9 w-9 p-0"
                onClick={handleDeleteImage}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </>
        ) : (
          <ImageUpload onImageUploaded={handleImageUploaded} />
        )}
      </div>
    </div>
  );
};

export default VehicleImage;
